/* eslint-disable complexity */
import type {
  BookingResponse,
  ValidationRules as ApiValidationRules,
} from '@models/landing/api/api-response.model';
import { mapBookingResponseToFlightDetails } from '@helper/flight-details.mapper';
import type {
  IncludedExtrasList,
  MappedEmergencyContact,
  MappedLeg,
  MappedPassenger,
  MappedPaxData,
} from '@models/landing/api/mapped-passenger.model';
import type {
  Airport,
  CodeTypeItem,
  CodeTypeList,
  MealsList,
} from '@models/master-data/master-data.model';
import {
  mapBookingResponseToCheckedInPassengers,
  mapBookingResponseToNotCheckedInPassengers,
} from '@helper/passenger-mapper';
import type {
  PaymentSection,
  PNRMetaData,
  ValidationRules as UiValidationRules,
} from '@models/ui/pnr-data-model';
import { mapContactDetails } from '@helper/contact-details.mapper';
import { mapOffloadPaxListFromBooking } from '@helper/cancel-check-in.view.mapper';
import { mapNotificationTargetsFromBooking } from '@helper/confirm-pnr.mapper';

// Derived types from BookingResponse to ensure ESLint type-checker resolves them correctly
type ApiPassenger = BookingResponse['paxList'][0];
type ApiEmergencyContact = NonNullable<ApiPassenger['emergencyContact']>;
type ApiFlight = BookingResponse['flights'][0];
type ApiLeg = ApiFlight['legs'][0];
type ApiPaxEntry = ApiLeg['pax'][0];
type ApiIncludedExtra = ApiPaxEntry['includedExtras'][0];

const SSR_TYPE_SEAT = 'SEAT';
const SSR_TYPE_MEAL = 'MEAL';
const SSR_TYPE_IFE = 'IFE';
const SSR_TYPE_LOUNGE = 'LOUNGE';
const SSR_TYPE_OTHERS = 'OTHERS';
const SSR_TYPE_BAGGAGE = 'BAGGAGE';
const SSR_TYPE_HANDBAGGAGE = 'HANDBAGGAGE';

const INFANT_PASSENGER_TYPE = 5;

export const toTitleCase = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const mapEmergencyContact = (
  ec: ApiEmergencyContact | null,
  isPrimaryPassenger: boolean,
): MappedEmergencyContact => {
  if (ec === null) {
    return {
      isDisclosed: false,
      contactRelation: '',
      contactName: '',
      contactNumber: '',
      countryCode: '',
      isMasked: false,
      isRelMasked: false,
      applyAll: false,
    };
  }

  return {
    isDisclosed: isPrimaryPassenger ? true : ec.isDisclosed,
    contactRelation: ec.contactRelation,
    contactName: ec.contactName,
    contactNumber: ec.contactNumber,
    countryCode: ec.countryCode,
    isMasked: false,
    isRelMasked: false,
    applyAll: false,
  };
};

const buildIncludedExtrasList = (extras: ApiIncludedExtra[]): IncludedExtrasList => {
  const bagItems = extras.filter(
    (e) => e.ssrType === SSR_TYPE_HANDBAGGAGE || e.ssrType === SSR_TYPE_BAGGAGE,
  );
  const loungeItems = extras.filter((e) => e.ssrType === SSR_TYPE_LOUNGE);
  const onlyHandbaggage =
    bagItems.length > 0 && !bagItems.some((e) => e.ssrType === SSR_TYPE_BAGGAGE);

  return {
    OnlyHandbaggage: onlyHandbaggage,
    BAG: bagItems,
    LOUNGE: loungeItems,
    MEAL: extras.find((e) => e.ssrType === SSR_TYPE_MEAL) ?? null,
    IFE: extras.find((e) => e.ssrType === SSR_TYPE_IFE) ?? null,
    SEAT: extras.find((e) => e.ssrType === SSR_TYPE_SEAT) ?? null,
    OTHERS: extras.find((e) => e.ssrType === SSR_TYPE_OTHERS) ?? null,
  };
};

const buildPaxData = (paxEntry: ApiPaxEntry, isInfant: boolean): MappedPaxData => {
  const { paxRestrictions } = paxEntry;
  const includedExtrasList = buildIncludedExtrasList(paxEntry.includedExtras);
  const isBusiness = paxEntry.cabinClass === 'BUSINESS';

  const base: MappedPaxData = {
    paxRestrictions,
    status: paxEntry.status,
    boardingPass: paxEntry.boardingPass,
    includedExtras: paxEntry.includedExtras,
    cabinClass: paxEntry.cabinClass,
    fromTerminal: paxEntry.fromTerminal,
    priorityCode: paxEntry.priorityCode,
    depGate: paxEntry.depGate,
    tierName: paxEntry.tierName,
    tierID: paxEntry.tierID,
    isVisaRunPax: paxEntry.isVisaRunPax,
    paxJourneyId: paxEntry.paxJourneyId,
    resPaxId: paxEntry.resPaxId,
    recordNumber: paxEntry.recordNumber,
    sprintRefId: paxEntry.sprintRefId,
    parentID: paxEntry.parentID,
    includedExtrasList,
    isBusiness,
  };

  if (!isInfant) {
    return base;
  }

  return {
    ...base,
  };
};

const buildMappedLeg = (
  leg: ApiLeg,
  logicalFlightID: string,
  paxEntry: ApiPaxEntry,
  isInfant: boolean,
): MappedLeg => {
  const startCity = leg.originCity;
  const endCity = leg.destinationCity;
  const cabinLabel = toTitleCase(leg.cabinClass);
  const webFareTypeName = `${cabinLabel} (${leg.fare.webFareType})`;
  const flightDetails = `${leg.sellingCarrier}   ${leg.flightNum}-${startCity} to ${endCity}`;
  const paxData = buildPaxData(paxEntry, isInfant);
  const hasLounge = paxEntry.includedExtras.some((e) => e.ssrType === SSR_TYPE_LOUNGE);
  const showLounge = leg.enableLoungeSale && hasLounge;

  return {
    waitTime: leg.waitTime,
    physicalFlightID: leg.physicalFlightID,
    arrivalDate: leg.arrivalDate,
    originCity: leg.originCity,
    departureDate: leg.departureDate,
    sellingCarrier: leg.sellingCarrier,
    flightNum: leg.flightNum,
    operatingCarrier: leg.operatingCarrier,
    cabinClass: leg.cabinClass,
    destinationTerminal: leg.destinationTerminal,
    origin: leg.origin,
    isCodeShare: leg.isCodeShare,
    isInterline: leg.isInterline,
    originTerminal: leg.originTerminal,
    duration: leg.duration,
    displayFlightNumber: leg.displayFlightNumber,
    destination: leg.destination,
    destinationCity: leg.destinationCity,
    segmentId: leg.segmentId,
    sellingCarrierFlightNumber: leg.sellingCarrierFlightNumber,
    operatedByOA: leg.operatedByOA,
    operatedByMessage: leg.operatedByMessage,
    equipmentType: leg.equipmentType,
    fare: leg.fare,
    stops: leg.stops,
    numberOfStops: leg.numberOfStops,
    seatInclusiveFare: leg.seatInclusiveFare,
    sprint: leg.sprint,
    uniqueKey: leg.uniqueKey,
    enableLoungeSale: leg.enableLoungeSale,
    isIFEBusinessRestricted: leg.isIFEBusinessRestricted,
    originOffset: leg.originOffset,
    isDisrupted: leg.isDisrupted,
    isPetIncluded: leg.isPetIncluded,
    isWithinCutOff: leg.isWithinCutOff,
    startCity,
    endCity,
    flightDetails,
    webFareTypeName,
    logicalFlightID,
    paxData,
    showLounge,
  };
};

const buildLegsForPassenger = (
  flights: ApiFlight[],
  resPaxId: number,
  isInfant: boolean,
): MappedLeg[] => {
  const result: MappedLeg[] = [];

  for (const flight of flights) {
    for (const leg of flight.legs) {
      const paxEntry = leg.pax.find((p) => p.resPaxId === resPaxId);
      if (paxEntry !== undefined) {
        result.push(buildMappedLeg(leg, flight.logicalFlightID, paxEntry, isInfant));
      }
    }
  }

  return result;
};

const aggregateRestrictionFlag = (
  legs: MappedLeg[],
  flag: keyof Pick<
    ApiPaxEntry['paxRestrictions'],
    | 'allowedCheckin'
    | 'allowedOffload'
    | 'allowedBoardingPass'
    | 'allowedEmail'
    | 'allowedSMS'
    | 'allowedWAPP'
  >,
): boolean => legs.every((leg) => leg.paxData.paxRestrictions?.[flag] ?? false);

const countTotalLegs = (flights: ApiFlight[]): number =>
  flights.reduce((sum, flight) => sum + flight.legs.length, 0);

const findInfantForPassenger = (
  paxList: ApiPassenger[],
  resPaxId: number,
): ApiPassenger | undefined =>
  paxList.find(
    (p) => p.travelsWithPassengerId === resPaxId && p.passengerType === INFANT_PASSENGER_TYPE,
  );

const findParentPassenger = (
  paxList: ApiPassenger[],
  travelsWithPassengerId: number,
): ApiPassenger | undefined => paxList.find((p) => p.resPaxId === travelsWithPassengerId);

const buildFullName = (title: string, firstName: string, lastName: string): string =>
  `${title}. ${firstName}    ${lastName}`;

const resolveLeftBorderColor = (legs: MappedLeg[]): string => {
  const firstLeg = legs[0];
  if (firstLeg === undefined) {
    return 'default';
  }

  const status = firstLeg.paxData.status;

  if (status === 'CheckedIn') {
    return 'green';
  }

  return 'default';
};

const buildPassengerBase = (
  pax: ApiPassenger,
  flights: ApiFlight[],
): Omit<
  MappedPassenger,
  | 'isAdult'
  | 'isInfant'
  | 'hasInfant'
  | 'infantResPaxId'
  | 'isInfantAssignedToPrimaryPax'
  | 'infantAssignedPaxAgeGroup'
> => {
  const isInfant = pax.passengerType === INFANT_PASSENGER_TYPE;
  const legs = buildLegsForPassenger(flights, pax.resPaxId, isInfant);
  const checkedInFlightsCount = countTotalLegs(flights);

  return {
    title: pax.title,
    middleName: pax.middleName,
    lastName: pax.lastName,
    passengerType: pax.passengerType,
    gender: pax.gender,
    firstName: pax.firstName,
    fFNum: pax.fFNum,
    isPrimaryPassenger: pax.isPrimaryPassenger,
    travelsWithPassengerId: pax.travelsWithPassengerId,
    tier: pax.tier,
    emergencyContact: mapEmergencyContact(pax.emergencyContact, pax.isPrimaryPassenger),
    isApisOk: pax.isApisOk,
    apisInfo: pax.apisInfo,
    contactInfo: pax.contactInfo,
    isGCCNational: pax.isGCCNational,
    isEUNational: pax.isEUNational,
    setApis: pax.setApis,
    IsCancelled: pax.IsCancelled,
    FlightGroup: pax.FlightGroup,
    isVisaRunPax: pax.isVisaRunPax,
    paxJourneyId: pax.paxJourneyId,
    resPaxId: pax.resPaxId,
    recordNumber: pax.recordNumber,
    sprintRefId: pax.sprintRefId,
    parentID: pax.parentID,
    isAllowCheckInCount: 0,
    checkedInFlightsCount,
    standByFlightsCount: 0,
    standByID90: 0,
    legs,
    allowedCheckin: aggregateRestrictionFlag(legs, 'allowedCheckin'),
    allowedOffload: aggregateRestrictionFlag(legs, 'allowedOffload'),
    allowedBoardingPass: aggregateRestrictionFlag(legs, 'allowedBoardingPass'),
    allowedEmail: aggregateRestrictionFlag(legs, 'allowedEmail'),
    allowedSMS: aggregateRestrictionFlag(legs, 'allowedSMS'),
    allowedWAPP: aggregateRestrictionFlag(legs, 'allowedWAPP'),
    leftBorderColor: resolveLeftBorderColor(legs),
    ischeckInSelected: false,
    fullName: buildFullName(pax.title, pax.firstName, pax.lastName),
  };
};

const mapInfantPassenger = (
  pax: ApiPassenger,
  base: ReturnType<typeof buildPassengerBase>,
  paxList: ApiPassenger[],
): MappedPassenger => {
  const parentPax =
    pax.travelsWithPassengerId !== null
      ? findParentPassenger(paxList, pax.travelsWithPassengerId)
      : undefined;

  return {
    ...base,
    isInfant: true,
    isInfantAssignedToPrimaryPax: parentPax?.isPrimaryPassenger ?? false,
    infantAssignedPaxAgeGroup: parentPax?.passengerType ?? 1,
  };
};

const mapAdultPassenger = (
  pax: ApiPassenger,
  base: ReturnType<typeof buildPassengerBase>,
  paxList: ApiPassenger[],
): MappedPassenger => {
  const infantPax = findInfantForPassenger(paxList, pax.resPaxId);

  if (infantPax !== undefined) {
    return { ...base, isAdult: true, hasInfant: true, infantResPaxId: infantPax.resPaxId };
  }

  return { ...base, isAdult: true };
};

const mapPassenger = (
  pax: ApiPassenger,
  flights: ApiFlight[],
  paxList: ApiPassenger[],
): MappedPassenger => {
  const base = buildPassengerBase(pax, flights);

  if (pax.passengerType === INFANT_PASSENGER_TYPE) {
    return mapInfantPassenger(pax, base, paxList);
  }

  return mapAdultPassenger(pax, base, paxList);
};

export const mapBookingResponseToPassengers = (response: BookingResponse): MappedPassenger[] =>
  response.paxList.map((pax) => mapPassenger(pax, response.flights, response.paxList));

const getPaymentDetails = (response: BookingResponse): PaymentSection => ({
  bookingCurrency: response.bookingCurrency,
  isPayLater: response.validationRules.isPayLater,
  paymentInProgress: response.paymentInProgress,
  dueAmountFormated: response.dueAmountFormated,
  reservationBalance: response.reservationBalance,
});

/**
 * Maps API ValidationRules to UI ValidationRules
 * Handles the API typo: apisFileds → apisFields
 *
 * @param apiValidationRules - Validation rules from API response
 * @returns Mapped validation rules for UI consumption
 */
export const mapValidationRules = (
  apiValidationRules?: ApiValidationRules | null,
  response?: BookingResponse,
): UiValidationRules | undefined => {
  if (!apiValidationRules) {
    return undefined;
  }

  return {
    isLoungePurchaseAllowed:
      response?.flights.some((flight) =>
        flight.legs.some((leg) =>
          leg.pax.some((paxEntry) => paxEntry.paxRestrictions?.allowedLounge ?? false),
        ),
      ) ?? false,
    isCheckInAllowed: !apiValidationRules.isWaitListed,
    isModifyAllowed: !apiValidationRules.isGDS,
    isCancelAllowed: !apiValidationRules.isGDS,
    isPayLater: apiValidationRules.isPayLater ?? false,
    isGroupPnr: Boolean(apiValidationRules.isGroupPnr),
    isAsyncPnr: Boolean(apiValidationRules.isAsyncPnr),
    gccList: apiValidationRules.gccList ?? [],
    euList: apiValidationRules.euList ?? [],
    // Transform apisFileds (API typo) → apisFields (corrected for UI)
    apisFields: apiValidationRules.apisFileds ?? undefined,
    isSeatWithinCutOff: apiValidationRules.CutoffEligibility?.[0]?.IsSeatWithinCutoff ?? false,
    isMealsWithinCutOff: apiValidationRules.CutoffEligibility?.[0]?.IsMealsWithinCutoff ?? false,
    isLoungeWithinCutOff: apiValidationRules.CutoffEligibility?.[0]?.IsLoungeWithinCutoff ?? false,
    isBaggageWithinCutOff:
      apiValidationRules.CutoffEligibility?.[0]?.IsBaggageWithinCutoff ?? false,
    warningTime: apiValidationRules.warningTime,
  };
};

export const mapPNRMetaData = (
  response: BookingResponse,
  _codeTypeList: CodeTypeList,
  airports: Airport[],
  mealsList: MealsList,
): PNRMetaData => {
  const checkedInPaxList = mapBookingResponseToCheckedInPassengers(
    response,
    _codeTypeList,
    airports,
    mealsList,
  );
  const notCheckedInPaxList = mapBookingResponseToNotCheckedInPassengers(
    response,
    _codeTypeList as CodeTypeItem[],
    mealsList,
  );
  const contactDetails = mapContactDetails(response);
  return {
    flightDetails: mapBookingResponseToFlightDetails(response),
    paymentDetails: getPaymentDetails(response),
    contactDetails: contactDetails,
    offloadPaxList: mapOffloadPaxListFromBooking(response, checkedInPaxList),
    notificationTargets: mapNotificationTargetsFromBooking(response, checkedInPaxList),
    checkedInPaxList: checkedInPaxList,
    notCheckedInPaxList: notCheckedInPaxList,
    groupPnrFlow: response.groupPnrFlow,
    bookingReference: response?.confirmationNumber,
    validationRules: mapValidationRules(response.validationRules, response),
    selectedSSRList: response.selectedSSRList,
    bookingCurrency: response.bookingCurrency,
    currencyDecimalPlaces: response.currencyDecimalPlaces,

    remainingTimeToCheckin: response.remainingTimeToCheckin,
    reservationBalance: response.reservationBalance,
  };
};
